import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, 
  Flex, 
  Text, 
  Icon,
  Heading,
  SimpleGrid,
  Skeleton,
  VStack,
  HStack,
  Divider,
  useColorModeValue,
  Badge
} from '@chakra-ui/react';
import { 
  WiDaySunny, 
  WiRain, 
  WiCloudy, 
  WiSnow, 
  WiThunderstorm, 
  WiFog, 
  WiDayCloudy,
  WiHumidity,
  WiStrongWind
} from 'react-icons/wi';
import { MdLocationOn } from 'react-icons/md';
import { OPEN_WEATHER_API_KEY } from '../../config/api_keys';
import './Weather.css';

const MobileWeatherWidget = () => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());

  // Use API key from config
  const API_KEY = OPEN_WEATHER_API_KEY;

  useEffect(() => {
    // Update current date and time
    setCurrentDate(new Date());
    
    const fetchWeather = async () => {
      try {
        // Use geolocation to get user's position
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            const url = `https://api.openweathermap.org/data/2.5/weather?lat=${latitude}&lon=${longitude}&units=metric&appid=${API_KEY}`;
            
            try {
              const response = await axios.get(url);
              setWeather(response.data);
            } catch (err) {
              setError('Error fetching weather data');
              console.error(err);
            } finally {
              setLoading(false);
            }
          },
          (error) => {
            setError('Location access denied');
            setLoading(false);
          }
        );
      } catch (err) {
        setError('Error fetching weather data');
        console.error(err);
        setLoading(false);
      }
    };

    fetchWeather();
  }, [API_KEY]);

  // Function to get the appropriate weather icon
  const getWeatherIcon = (weatherCode) => {
    if (!weatherCode) return WiCloudy;
    
    // Weather condition codes: https://openweathermap.org/weather-conditions
    if (weatherCode >= 200 && weatherCode < 300) return WiThunderstorm;
    if (weatherCode >= 300 && weatherCode < 400) return WiRain;
    if (weatherCode >= 500 && weatherCode < 600) return WiRain;
    if (weatherCode >= 600 && weatherCode < 700) return WiSnow;
    if (weatherCode >= 700 && weatherCode < 800) return WiFog;
    if (weatherCode === 800) return WiDaySunny;
    if (weatherCode > 800) return WiDayCloudy;
    
    return WiCloudy; // Default icon
  };

  // Function to get farming advice based on weather conditions
  const getFarmingAdvice = (weatherCode, temp, humidity) => {
    if (weatherCode >= 500 && weatherCode < 600) {
      return "Avoid field work today due to rain.";
    } else if (weatherCode === 800 && temp > 25 && humidity > 60) {
      return "Good conditions for pesticide application.";
    } else if (weatherCode >= 801 && weatherCode <= 804) {
      return "Ideal day for pesticide application.";
    } else if (temp > 30) {
      return "Ensure adequate irrigation for crops.";
    } else {
      return "Good conditions for general field work.";
    }
  };

  if (loading) {
    return (
      <Box 
        p={3} 
        w="100%" 
        borderRadius="xl"
      >
        <Skeleton height="100px" />
      </Box>
    );
  }

  if (error || !weather) {
    return null;
  }

  const WeatherIcon = getWeatherIcon(weather.weather[0].id);
  const formattedDate = currentDate.toLocaleDateString('en-US', { 
    day: '2-digit', 
    month: 'short'
  });
  
  const weatherCondition = weather.weather[0].main;
  const farmingAdvice = getFarmingAdvice(
    weather.weather[0].id, 
    weather.main.temp, 
    weather.main.humidity
  );

  return (
    <Box className="mobile-weather-widget-compact">
      {/* Weather Card */}
      <Box 
        className="weather-card-compact"
        borderRadius="xl"
        overflow="hidden"
        mb={3}
        bgGradient="linear(to-r, green.400, teal.500)"
        boxShadow="lg"
      >
        <Flex p={3}>
          {/* Left side - Location and Temperature */}
          <Flex direction="column" flex={1}>
            <Flex align="center" mb={2}>
              <Icon as={MdLocationOn} boxSize={3} color="white" mr={1} />
              <Text fontSize="xs" fontWeight="medium" color="white" opacity={0.9}>
                {weather.name}
              </Text>
            </Flex>
            
            <Flex align="baseline" mb={1}>
              <Text fontSize="2xl" fontWeight="bold" color="white" lineHeight="1">
                {Math.round(weather.main.temp)}°C
              </Text>
              <Text fontSize="xs" color="white" opacity={0.8} ml={2}>
                {formattedDate}
              </Text>
            </Flex>
            
            <Text fontSize="xs" color="white" opacity={0.8} textTransform="capitalize">
              {weather.weather[0].description}
            </Text>
          </Flex>

          {/* Right side - Weather Icon and Details */}
          <Flex direction="column" align="flex-end" justify="space-between">
            <Icon as={WeatherIcon} boxSize={10} color="white" />
            
            <VStack spacing={1} align="flex-end">
              <HStack spacing={2}>
                <Icon as={WiHumidity} boxSize={3} color="white" opacity={0.8} />
                <Text fontSize="xs" color="white" opacity={0.9}>
                  {weather.main.humidity}%
                </Text>
              </HStack>
              <HStack spacing={2}>
                <Icon as={WiStrongWind} boxSize={3} color="white" opacity={0.8} />
                <Text fontSize="xs" color="white" opacity={0.9}>
                  {Math.round(weather.wind.speed)} m/s
                </Text>
              </HStack>
            </VStack>
          </Flex>
        </Flex>
        
        {/* Farming Advice */}
        <Box bg="rgba(255,255,255,0.1)" px={3} py={2}>
          <Flex align="center">
            <Box w={2} h={2} borderRadius="full" bg="yellow.300" mr={2}></Box>
            <Text fontSize="xs" color="white" opacity={0.9} fontStyle="italic">
              {farmingAdvice}
            </Text>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default MobileWeatherWidget; 